import { createReducer, on } from '@ngrx/store';
import { PupilAttendanceSchedule, SchoolCalendarException } from '@core/models/domain/school-calendar.domain';
import {
  SchoolCalendarExceptionRequest,
  SetPupilAttendanceScheduleRequest
} from '@core/models/request/school-calendar.request';
import * as SchoolCalendarActions from './school-calendar.actions';
import { initialSchoolCalendarState, SchoolCalendarState } from './school-calendar.state';

/**
 * The entry the page shows the instant it is saved, built from what was sent.
 *
 * <p>It stands in for the backend's own copy until a read returns it: a write is
 * a command the backend projects into its read model a moment later, and waiting
 * for that would leave the admin looking at a list that does not yet contain
 * what they just added. Only the fields the calendar itself displays are filled
 * in; the backend remains the authority on the rest, and its version replaces
 * this one as soon as it arrives.</p>
 */
function optimisticException(request: SchoolCalendarExceptionRequest): SchoolCalendarException {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: request.exceptionId ?? `pending-${Date.now()}`,
    name: request.name,
    description: request.description,
    type: request.type,
    scope: request.scope,
    yearGroups: request.yearGroups,
    groupIds: request.groupIds,
    pupilAccountIds: request.pupilAccountIds,
    // An emergency entry starts the moment it is saved, and the server stamps the time.
    startDate: request.emergency ? today : request.startDate,
    endDate: request.endDate,
    startTime: request.startTime,
    endTime: request.endTime,
    emergency: request.emergency
  } as SchoolCalendarException;
}

function optimisticSchedule(request: SetPupilAttendanceScheduleRequest): PupilAttendanceSchedule {
  return {
    pupilAccountId: request.pupilAccountId,
    attendance: request.attendance,
    validFrom: request.validFrom,
    validUntil: request.validUntil,
    active: true
  };
}

/** True once a read has come back carrying the write this entry stood in for. */
function readNowHasIt(read: SchoolCalendarException[], pending: SchoolCalendarException): boolean {
  return read.some((exception) =>
    exception.id === pending.id
    || (exception.name === pending.name
      && exception.scope === pending.scope
      && exception.startDate === pending.startDate));
}

/**
 * A withdrawn exception is marked cancelled in place rather than removed: the calendar keeps
 * showing it, greyed out, so an admin can see why restrictions were suspended and that the
 * suspension has since been lifted.
 */
export const schoolCalendarReducer = createReducer<SchoolCalendarState>(
  initialSchoolCalendarState,

  on(SchoolCalendarActions.loadExceptions, (state) => ({ ...state, loading: true, error: null })),
  on(SchoolCalendarActions.loadExceptionsSuccess, (state, { exceptions }) => ({
    ...state,
    exceptions,
    pendingExceptions: state.pendingExceptions.filter((pending) => !readNowHasIt(exceptions, pending)),
    loading: false,
    error: null,
  })),
  on(SchoolCalendarActions.loadExceptionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Shown straight away, so the calendar and the list carry the entry the moment it is saved.
  on(SchoolCalendarActions.saveException, (state, { request }) => ({
    ...state,
    pendingExceptions: [
      ...state.pendingExceptions.filter((pending) => pending.id !== request.exceptionId),
      optimisticException(request)
    ],
    saving: true,
    error: null,
  })),
  on(SchoolCalendarActions.saveExceptionSuccess, (state) => ({ ...state, saving: false })),
  // The save did not happen, so what it showed has to go with it.
  on(SchoolCalendarActions.saveExceptionFailure, (state, { error, request }) => ({
    ...state,
    pendingExceptions: request
      ? state.pendingExceptions.filter((pending) => !readNowHasIt([optimisticException(request)], pending))
      : [],
    saving: false,
    error,
  })),

  on(SchoolCalendarActions.cancelException, (state, { exceptionId }) => ({
    ...state,
    cancellingId: exceptionId,
    error: null,
  })),
  on(SchoolCalendarActions.cancelExceptionSuccess, (state, { exceptionId }) => ({
    ...state,
    cancellingId: null,
    exceptions: state.exceptions.map((exception) =>
      exception.id === exceptionId ? { ...exception, cancelled: true } : exception
    ),
    pendingExceptions: state.pendingExceptions.map((pending) =>
      pending.id === exceptionId ? { ...pending, cancelled: true } : pending
    ),
  })),
  on(SchoolCalendarActions.cancelExceptionFailure, (state, { error }) => ({
    ...state,
    cancellingId: null,
    error,
  })),

  on(SchoolCalendarActions.loadSchedules, (state) => ({ ...state, loading: true, error: null })),
  on(SchoolCalendarActions.loadSchedulesSuccess, (state, { schedules }) => ({
    ...state,
    schedules,
    pendingSchedules: state.pendingSchedules.filter(
      (pending) => !schedules.some((schedule) => schedule.pupilAccountId === pending.pupilAccountId)
    ),
    pendingScheduleRemovals: state.pendingScheduleRemovals.filter(
      (pupilAccountId) => schedules.some((schedule) => schedule.pupilAccountId === pupilAccountId)
    ),
    loading: false,
    error: null,
  })),
  on(SchoolCalendarActions.loadSchedulesFailure, (state, { error }) => ({ ...state, loading: false, error })),

  // Both are shown straight away for the same reason a saved entry is.
  on(SchoolCalendarActions.saveSchedule, (state, { request }) => ({
    ...state,
    pendingSchedules: [
      ...state.pendingSchedules.filter((pending) => pending.pupilAccountId !== request.pupilAccountId),
      optimisticSchedule(request)
    ],
    pendingScheduleRemovals: state.pendingScheduleRemovals.filter((id) => id !== request.pupilAccountId),
    saving: true,
    error: null,
  })),
  on(SchoolCalendarActions.removeSchedule, (state, { pupilAccountId }) => ({
    ...state,
    pendingSchedules: state.pendingSchedules.filter((pending) => pending.pupilAccountId !== pupilAccountId),
    pendingScheduleRemovals: [...new Set([...state.pendingScheduleRemovals, pupilAccountId])],
    saving: true,
    error: null,
  })),
  on(SchoolCalendarActions.saveScheduleSuccess, SchoolCalendarActions.removeScheduleSuccess, (state) => ({
    ...state,
    saving: false,
  })),
  // Neither write happened, so the timetables go back to what the last read said.
  on(SchoolCalendarActions.saveScheduleFailure, SchoolCalendarActions.removeScheduleFailure, (state, { error }) => ({
    ...state,
    pendingSchedules: [],
    pendingScheduleRemovals: [],
    saving: false,
    error,
  }))
);
